/* @flow */
import * as React from 'react';
import { pick } from 'lodash';

import Divider from 'components/Divider';
import Glyph from 'components/Glyph';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButton';
import MenuItem from 'components/MenuItem';
import TextWithGlyph from 'components/TextWithGlyph';
import TextWithIcon from 'components/TextWithIcon';

import type { GlyphName } from '@remind/r101-glyphs';
import type { IconName } from 'components/Icon';

type ComposableTableMenuItem = {
  conditionToCheck: ?string,
  label: string,
  glyph?: GlyphName,
  icon?: IconName,
  fieldsForAction: ?(string[]),
  onClickAction: Function,
  divided?: boolean,
};

type Props = {
  data: Object,
  menuItems: ComposableTableMenuItem[],
};

function MenuButtonCell({ data, menuItems }: Props) {
  const items = menuItems
    .map(item => {
      if (item.conditionToCheck && !data[item.conditionToCheck]) {
        return null;
      }
      const fields = item.fieldsForAction ? pick(data, item.fieldsForAction) : undefined;

      return (
        <React.Fragment key={item.label}>
          {item.divided && (
            <MenuItem>
              <Divider size="flush" />
            </MenuItem>
          )}
          <MenuItem
            data-test={`menu-button-cell-${item.label}`}
            onClick={e => {
              e.stopPropagation();
              item.onClickAction(fields);
            }}>
            {(item.glyph && <TextWithGlyph glyph={item.glyph}>{item.label}</TextWithGlyph>) ||
              (item.icon && <TextWithIcon icon={item.icon}>{item.label}</TextWithIcon>)}
          </MenuItem>
        </React.Fragment>
      );
    })
    .filter(i => i);

  if (items.length === 0) {
    return null;
  }

  const menu = <Menu>{items}</Menu>;
  return (
    <MenuButton
      onClick={e => e.stopPropagation()}
      menu={menu}
      aria-label="More actions"
      level="icon"
      color="brand">
      <Glyph name="ellipses" size="medium" />
    </MenuButton>
  );
}

export function getMenuButtonCellGenerator(menuItems: ComposableTableMenuItem[]) {
  return (data: Object) => {
    return <MenuButtonCell data={data} menuItems={menuItems} />;
  };
}
